<template>
  <div>
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="800">
      <material-card
        :title="
          isNew
            ? $t('components.plancreationwizard.add_contribution_category')
            : $t('components.plancreationwizard.edit_contribution_category')
        "
        :color="$vuetify.theme.themes.light.secondary"
        icon="schedule"
      >
        <v-card-text v-if="dialogs.edit">
          <v-form ref="form" v-model="valid">
            <v-container fluid fill-height>
              <v-row wrap>
                <v-col cols="12" md="4">
                  <no-autocomplete-textfield
                    :label="$t('globals.category')"
                    v-model="item.Category"
                    :rules="[$rules.required]"
                    :class="'required'"
                  ></no-autocomplete-textfield>
                </v-col>
                <v-col cols="12" md="4">
                  <trans-multi-select2
                    v-model="item.Employers"
                    :items="calculatedEmployers()"
                    :label="$t('globals.employers')"
                    field="Name"
                    itemValue="Identifier"
                    :rules="[rules.atLeastRequired]"
                  ></trans-multi-select2>
                </v-col>
                <v-col cols="12" md="4" v-if="hasStores">
                  <trans-multi-select2
                    v-model="item.Stores"
                    :items="calculatedStores()"
                    :label="$t('globals.stores')"
                    field="Name"
                    itemValue="Identifier"
                    :rules="[rules.atLeastRequired]"
                  ></trans-multi-select2>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="close">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn :disabled="!valid" color="primary" @click="saveItem">
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog v-model="dialogs.delete" scrollable persistent max-width="400">
      <material-card
        :title="
          $t('components.plancreationwizard.remove_contribution_category')
        "
        :color="$vuetify.theme.themes.light.error"
        icon="remove"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="removeItem">
            {{ $t("globals.remove") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <material-card
      :title="$t('components.plancreationwizard.contribution_categories')"
      :color="$vuetify.theme.themes.light.secondary"
      icon="schedule"
      style="margin-top:30px"
    >
      <v-card-text>
        <pbas-data-table
          ref="table"
          :dataProvider="provider"
          :rowActions="rowActions"
          :floatActions="floatActions"
          fileNamePrefix="contributionCategories"
          identifier="contributionCategoriesTable"
          @error="error = $event"
          :showSelect="false"
        ></pbas-data-table>
      </v-card-text>
    </material-card>
    <error-handler :error="error"></error-handler>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import ContributionCategoriesHeaders from "@/headers/ContributionCategories";
import { InMemoryProvider } from "pbas-vue-components";
import NoAutocompleteTextfield from "../NoAutocompleteTextfield.vue";
import TransMultiSelect2 from "../TransMultiSelect2.vue";
import store from "../../store";
export default {
  components: {
    ErrorHandler,
    NoAutocompleteTextfield,
    TransMultiSelect2,
  },
  props: [
    "contributionCategories",
    "effectiveDate",
    "employers",
    "disabled",
    "hasStores",
  ],
  data() {
    return {
      item: null,
      floatActions: [
        {
          name: "addContributionCategory",
          icon: "add",
          color: this.$vuetify.theme.themes.light.success,
          text: "globals.add_new",
          handler: () => {
            this.item = {
              Identifier: Math.random().toString(),
              Term: {},
            };
            this.isNew = true;
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
      ],
      rowActions: [
        {
          name: "editContributionCategory",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.item = JSON.parse(JSON.stringify(item));
            this.originalItem = JSON.parse(JSON.stringify(item));
            this.isNew = false;
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
        {
          name: "removeContributionCategory",
          icon: "delete",
          title: "globals.remove",
          handler: (item) => {
            this.item = JSON.parse(JSON.stringify(item));
            this.dialogs.delete = true;
          },
          visible: () => !this.disabled,
        },
      ],
      provider: undefined,
      headers: ContributionCategoriesHeaders,
      error: null,
      isNew: false,
      valid: false,
      dialogs: {
        edit: false,
        delete: false,
      },
      rules: {
        atLeastRequired: (v) => {
          if (v === undefined || v === null || v === "") {
            return true;
          }
          if (this.item === undefined || this.item === null) {
            return true;
          }
          return (
            (this.item.Employers && this.item.Employers.length > 0) ||
            (this.item.Stores && this.item.Stores.length > 0) ||
            this.$t(
              "validation.at_least_one_employer_or_store_should_be_selected"
            )
          );
        },
      },
      originalItem: undefined,
    };
  },
  computed: {
    ...mapGetters("plansMembers", ["options"]),
    stores() {
      return [].concat.apply([], this.employers.map((o) => o.Stores));
    },
  },
  watch: {
    contributionCategories: {
      handler(v) {
        if (this.provider !== null && this.provider !== undefined) {
          this.provider.items = v;
          this.$refs.table.getDataAsync();
        } else {
          this.provider = new InMemoryProvider(v, this.headers);
        }
      },
    },
    "item.Employers": {
      handler() {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      },
      immediate: true,
      deep: true,
    },
    "item.Stores": {
      handler() {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      },
      immediate: true,
      deep: true,
    },
    hasStores: {
      handler(v) {
        if (!v) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let cc of this.provider.items) {
              cc.Stores = [];
            }
          }
          this.$emit("update-contribution-categories", this.provider.items);
        }
      },
    },
  },
  mounted() {
    if (
      this.contributionCategories !== null &&
      this.contributionCategories !== undefined
    ) {
      this.provider = new InMemoryProvider(
        this.contributionCategories,
        this.headers
      );
    }
  },
  methods: {
    calculatedEmployers() {
      let employers = [];
      if (this.isNew) {
        employers = this.employers.filter(
          (o) =>
            this.provider.items.filter(
              (e) =>
                e.Employers &&
                e.Employers.filter((s) => s.Identifier === o.Identifier)
                  .length > 0
            ).length === 0
        );
      } else {
        employers = this.employers.filter(
          (o) =>
            this.provider.items.filter(
              (e) =>
                e.Employers &&
                e.Employers.filter((s) => s.Identifier === o.Identifier)
                  .length > 0
            ).length === 0 ||
            (this.originalItem.Employers &&
              this.originalItem.Employers.filter(
                (e) => e.Identifier === o.Identifier
              ).length > 0)
        );
      }
      let retEmployers = [];
      for (let employer of employers) {
        console.log(employer);
        let found = false;
        for (let item of this.provider.items) {
          if (item.Stores && employer.Stores) {
            console.log(item.Stores, employer.Stores);
            if (
              employer.Stores.filter(
                (s) =>
                  item.Stores.filter((ss) => ss.Identifier === s.Identifier)
                    .length > 0
              ).length > 0
            ) {
              console.log(employer);
              found = true;
              break;
            }
          }
        }
        if (this.item.Stores) {
          for (let store of this.item.Stores) {
            if (employer.Stores.filter(s=>s.Identifier === store.Identifier).length > 0) {
              found = true;
              break;
            }
          }
        }
        if (!found) {
          console.log(employer);
          retEmployers.push(employer);
        }
      }
      return retEmployers;
    },
    calculatedStores() {
      console.log("stores: ", this.stores);
      let stores = [];
      if (this.isNew) {
        stores = this.stores.filter(
          (o) =>
            this.provider.items.filter(
              (e) =>
                e.Stores &&
                e.Stores.filter((s) => s.Identifier === o.Identifier).length > 0
            ).length === 0
        );
      } else {
        stores = this.stores.filter(
          (o) =>
            this.provider.items.filter(
              (e) =>
                e.Stores &&
                e.Stores.filter((s) => s.Identifier === o.Identifier).length > 0
            ).length === 0 ||
            (this.item.Stores &&
              this.item.Stores.filter((e) => e.Identifier === o.Identifier)
                .length > 0)
        );
      }

      for (let item of this.provider.items) {
        if (item.Employers) {
          for (let employer of item.Employers) {
            for (let store of employer.Stores) {
              const itemStores = stores.filter(
                (s) => s.Identifier === store.Identifier
              );
              if (itemStores.length > 0) {
                const index = stores.indexOf(itemStores[0]);
                if (index > -1) {
                  stores.splice(index, 1);
                }
              }
            }
          }
        }
      }

      if (this.item.Employers !== null && this.item.Employers !== undefined) {
        stores = stores.filter(
          (s) =>
            this.item.Employers.filter(
              (e) =>
                e.Stores.filter((ss) => ss.Identifier === s.Identifier).length >
                0
            ).length === 0
        );
      }

      console.log(stores);
      if (this.item.Employers) {
        for (let employer of this.item.Employers) {
          for (let store of employer.Stores) {
            if (this.item.Stores) {
              const itemStores = this.item.Stores.filter(
                (s) => s.Identifier === store.Identifier
              );
              if (itemStores.length > 0) {
                const index = this.item.Stores.indexOf(itemStores[0]);
                if (index > -1) {
                  this.item.Stores.splice(index, 1);
                }
              }
            }
          }
        }
      }
      return stores;
    },
    saveItem() {
      let item = JSON.parse(JSON.stringify(this.item));
      // item.EffectiveDate = this.effectiveDate;
      if (this.isNew) {
        this.addItem(item);
      } else {
        this.updateItem(item);
      }
    },
    updateItem(item) {
      let contributionCategoryItem;
      contributionCategoryItem = this.provider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      this.provider.items[
        this.provider.items.indexOf(contributionCategoryItem)
      ] = item;

      this.close();
      this.$refs.table.getDataAsync();
      this.$emit("update-contribution-categories", this.provider.items);
    },
    addItem(item) {
      this.provider.items.push(item);
      this.close();
      this.$refs.table.getDataAsync();
      this.$emit("update-contribution-categories", this.provider.items);
    },
    removeItem() {
      let item = JSON.parse(JSON.stringify(this.item));
      let contributionCategoryItem;
      contributionCategoryItem = this.provider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      this.provider.items.splice(
        this.provider.items.indexOf(contributionCategoryItem),
        1
      );
      this.close();
      this.$refs.table.getDataAsync();
      this.$emit("update-contribution-categories", this.provider.items);
    },
    close() {
      this.dialogs.edit = false;
      this.dialogs.delete = false;
      this.item = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
  },
};
</script>

<style></style>
